import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Mail, ArrowRight } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import XionLogo from "../components/XionLogo";
import PasswordInput from "../components/PasswordInput";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    if (res.user.role === "admin") navigate("/admin");
    else navigate("/dashboard");
  };

  return (
    <div className="min-h-screen bg-[#F8F8F6] text-[#0E0E1A] grid lg:grid-cols-2">
      <div className="relative hidden lg:block overflow-hidden border-r border-black/[0.06]">
        <img src="https://static.prod-images.emergentagent.com/jobs/41ef69e5-e4fb-4fd7-8f79-f334d2b03553/images/d7abb13dc339e3a25dc8caf65fcbc0710665dc5c378162ded4a0c9c4c5b1bd80.png" alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#06060C]/30 to-[#06060C]" />
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="relative h-full p-12 flex flex-col justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <XionLogo size={36} />
            <span className="font-display font-black text-2xl tracking-tighter">XION LABS</span>
          </Link>
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#00D4FF] mb-3">/ welcome back</p>
            <h2 className="font-display font-black text-5xl tracking-tight max-w-md leading-tight">
              The studio you<br /><span className="text-gradient-cyan">log into</span>, not log out of.
            </h2>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          className="w-full max-w-md"
        >
          <Link to="/" className="lg:hidden flex items-center gap-2.5 mb-10">
            <XionLogo size={30} />
            <span className="font-display font-black text-xl tracking-tighter">XION LABS</span>
          </Link>

          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#00D4FF]">/ sign in</p>
          <h1 className="font-display font-black text-4xl mt-2 mb-2">Welcome back.</h1>
          <p className="text-[#5A5A72] mb-10">Track your projects and quote history.</p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="text-xs font-mono uppercase tracking-[0.2em] text-[#5A5A72]">Email</label>
              <div className="mt-2 relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B7280]" />
                <input
                  type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email-input"
                  className="w-full pl-11 pr-4 py-3.5 rounded-lg bg-white border border-black/[0.08] focus:border-[#00D4FF]/60 focus:ring-2 focus:ring-cyan-400/20 outline-none transition"
                  placeholder="you@studio.com"
                />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-mono uppercase tracking-[0.2em] text-[#5A5A72]">Password</label>
                <Link to="/forgot-password" className="text-xs text-[#2060EE] hover:underline">Forgot password?</Link>
              </div>
              <div className="relative">
                <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} testId="login-password-input" autoComplete="current-password" />
              </div>
            </div>
            {error && (
              <div data-testid="login-error" className="text-sm text-red-400 bg-red-500/5 border border-red-500/20 rounded-lg px-4 py-3">{error}</div>
            )}
            <button
              type="submit" disabled={loading}
              data-testid="login-submit-btn"
              className="w-full btn-primary py-3.5 rounded-lg font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading ? "Signing in..." : <>Sign in <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>

          <p className="mt-8 text-center text-sm text-[#5A5A72]">
            New to Xion?{" "}
            <Link to="/register" data-testid="login-to-register" className="text-[#00D4FF] hover:underline">Create an account</Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
